import {
  Component,
  ViewEncapsulation,
  Injector,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { FormBuilderService } from '../../../../core/services/form-builder.service';
import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  alphaNanoIdUtil,
  CustomIdInputTypesIdLength,
  EIremboFormlyFieldTypes,
  IIrembogovBasicLabelKeyPair,
  LOCATION_WIDGET_LEVELS,
} from '@irembo-andela/irembogov3-common';
import { FormBuilderManipulatorClass } from '../../../../core/utils/form-builder-manipulator-class.utils';
import { EDragDropItemsIds } from '../../../../core/models/form-builder-drag-item-ids.enum';
import * as LocationWidgetUtils from '../../../../core/utils/form-fields-formly-configurations/location-widget-components-generator.util';
import { buildSlotWidgetField } from '../../../../core/utils/form-fields-formly-configurations/slot-booking-component-generator.utils';

@Component({
  selector: 'irembogov-form-field-widgets-list',
  templateUrl: './form-field-widgets-list.component.html',
  styleUrls: ['./form-field-widgets-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FormFieldWidgetsListComponent implements OnInit, OnDestroy {
  EDragDropItemsIds = EDragDropItemsIds;
  randomKey: string = alphaNanoIdUtil(5);

  formFieldWidgets: IIrembogovBasicLabelKeyPair<FormlyFieldConfig>[] = [
    {
      label: 'Application number',
      key: {
        key: 'ApplicationNumber',
        type: EIremboFormlyFieldTypes.customapplicationnumberinput,
        props: {
          label: 'Application number',
          required: true,
          url: '/application/v1/application/by-application-number/details',
          useBaseUrl: true,
          placeholder: 'Application number',
          appNoLength: 19,
          compareKey: 'serviceId',
          compareOperator: '===',
          compareValue: '',
          populates: [],
        },
        validation: {
          messages: {
            required: 'Error message for `required`',
            invalidInput: 'Error message for invalid input',
            invalidAppNo: 'Error message for invalid ID',
            compareStatus: 'Error message for invalid comparison',
          },
        },
      },
    },
    {
      label: 'Cascading Dropdown',
      key: {
        key: 'CascadingDropDown',
        type: EIremboFormlyFieldTypes.customcascadingdropdowns,
        props: {
          required: true,
          label: 'CascadingDropDown',
          configs: [
            {
              key: 'province',
              label: 'Province',
              bindlabel: 'name',
              bindvalue: '',
              placeholder: 'Select provinces',
              parent: null,
              required: true,
              dataset: {
                url: 'http://localhost:8000/location/api/v1/provinces',
                dataField: 'data.content',
              },
            },
            {
              key: 'district',
              label: 'District',
              bindlabel: 'name',
              bindvalue: '',
              placeholder: 'Select district',
              parent: 'province',
              required: true,
              parentBindKey: 'id',
              dataset: {
                url: 'http://localhost:8000/location/api/v1/districts',
                dataField: 'data.content',
              },
            },
            {
              key: 'sector',
              label: 'Sector',
              bindlabel: 'name',
              bindvalue: '',
              placeholder: 'Select sector',
              parent: 'district',
              required: true,
              parentBindKey: 'id',
              dataset: {
                url: 'http://localhost:8000/location/api/v1/sectors',
                dataField: 'data.content',
              },
            },
          ],
        },
      },
    },
    {
      label: 'Child ID',
      key: {
        key: 'Child_ID',
        type: EIremboFormlyFieldTypes.customidinput,
        props: {
          idType: 'CHILD_ID',
          useBaseUrl: true,
          label: 'Child National ID',
          disabled: false,
          readonly: false,
          idLength: CustomIdInputTypesIdLength.CHILD_ID_LENGTH,
          required: true,
          placeholder: 'Provide Child National ID',
          populates: [],
          endpointCode: 'NIDAGETIDINFO',
        },
      },
    },
    {
      label: 'CRVS Data Fetch',
      key: {
        key: 'CRVS',
        type: EIremboFormlyFieldTypes.customcrvsidinput,
        props: {
          label: 'CRVS Data Fetch',
          required: true,
          allowedDocumentTypes: [
            {
              value: 'CHILDID',
              pattern: '^\\d*$',
              minLength: 8,
            },
            {
              value: 'PASSPORTID',
              pattern: '^[a-zA-Z0-9]*$',
              minLength: 5,
            },
          ],
          documentTypeKey: '',
          placeholder: 'Enter identification number',
          useBaseUrl: true,
          endpointCode: 'NIDAGETIDINFO',
          populates: [],
        },
        validation: {
          messages: {
            required: 'Error message for `required`',
            invalidInput: 'Error message for invalid input',
            invalidId: 'Error message for failed response',
            pattern: 'Error message for pattern',
          },
        },
      },
    },
    {
      label: 'Data Table',
      key: {
        key: 'Data Table',
        type: EIremboFormlyFieldTypes.customdatatable,
        props: {
          label: 'Favourite Services',
          columns: [
            {
              key: 'name',
              title: 'Name',
              sortable: true,
            },
            {
              key: 'gender',
              title: 'Gender',
              sortable: false,
            },
          ],
          items: [
            {
              name: 'Stacy Mark',
              gender: 'Female',
            },
            {
              name: 'John Doe',
              gender: 'Male',
            },
            {
              name: 'Bayo Olatunji',
              gender: 'Male',
            },
          ],
          asyncData: false,
          searchable: true,
          selectable: true,
          minSelection: 1,
          maxSelection: 2,
          emptyMessage: 'No Records',
          disabled: false,
          searchPlaceHolder: 'Enter searck keyword',
          customTableClass: 'customTableClass',
          title: 'Title of the table',
          minimalPagination: false,
          pageSize: 20,
          zeroBasedPageIndex: true,
          currentPageKey: 'page',
          pageSizeKey: 'size',
          searchKeywordKey: 'searchTerm',
          collectionSizeKey: 'totalElements',
          sortKey: 'sort',
          sortDirectionKey: 'sortDirection',
          dataset: {
            httpMethod: 'GET',
            url: '/service/api/v1/public/services',
            headers: {},
            dataField: 'data.content',
            useBaseUrl: true,
            params: {
              sort: 'dateCreated',
              sortDirection: 'asc',
            },
            endpointCode: '',
          },
        },
        defaultValue: null,
        expressions: {},
        validation: {
          messages: {
            minSelection: 'At least one item should be selected',
            required: 'Field is required',
            maxSelection: 'You only allowed to select 2 items from the table',
          },
        },
      },
    },
    {
      label: 'Table with input block',
      key: {
        key: 'Table with input block',
        type: EIremboFormlyFieldTypes.customtablewithinputform,
        props: {
          label: 'Table with input block',
          columns: [
            {
              label: 'Name',
              key: 'name',
              type: 'input',
              required: true,
              unique: true,
            },
            { label: 'Date', key: 'date', type: 'customdatepicker' },
          ],
          enableEdit: true,
          enableDeletion: true,
          enableRowSelection: false,
          showSearch: false,
          showPagination: true,
          itemsPerPage: 10,
        },
      },
    },
    {
      label: 'Date Time Picker',
      key: {
        key: 'DatetimePicker',
        type: EIremboFormlyFieldTypes.customdatetimepicker,
        props: {
          label: 'Date time picker',
          disabled: false,
          required: false,
          placeholder: 'Date time picker',
        },
      },
    },
    {
      label: 'Double ID Input WIdget',
      key: {
        key: 'CustomDoubleIdInputHAQS2',
        id: 'formly_422_customdoubleidinput_CustomDoubleIdInputHAQS2_0',
        type: 'customdoubleidinput',
        props: {
          url: '/integration/v1/fetch/sync',
          label: '',
          disabled: false,
          required: true,
          populates: [
            {
              valueKey: 'husbandDetails.surName',
              targetKey: 'TextInputHAQS2',
            },
            {
              valueKey: 'civilStatusOfficeName',
              targetKey: 'Textinput2JEbTQ',
            },
          ],
          useBaseUrl: true,
          placeholder: 'Text Input',
          endpointCode: 'FGETMARRIAGERECORD',
          firstIdentityLabel: 'Husband',
          secondIdentityLabel: 'Wife',
          allowedDocumentTypes: [
            {
              label: 'National ID',
              value: 'NID',
              pattern: '^\\d*$',
              maxLength: 16,
              minLength: 16,
            },
            {
              label: 'NIN',
              value: 'NIN',
              pattern: '^\\d{3}-\\d{4}-\\d{3}$',
              maxLength: 12,
              minLength: 12,
              placeholder: 'Enter NIN (XXX-XXXX-XXX)',
            },
            {
              label: 'Child ID',
              value: 'CHILD_ID',
              pattern: '^\\d*$',
              maxLength: 8,
              minLength: 8,
            },
          ],
          firstIdentityValueKey: 'husbandDocumentNumber',
          firstIdentityPlaceholder: "Husband's Details",
          firstIdentityDocumentTypeKey: 'husbandDocumentType',
          firstIdentityCountryValueKey: 'husbandCountry',
          secondIdentityValueKey: 'wifeDocumentNumber',
          secondIdentityPlaceholder: "Wife's Details",
          secondIdentityDocumentTypeKey: 'wifeDocumentType',
          secondIdentityCountryValueKey: 'wifeCountry',
        },
        defaultValue: null,
        expressions: {},
        validation: {
          messages: {
            required: 'Error message for required input',
            invalidInput: 'Error message for invalid input',
          },
        },
        wrappers: [
          'form-field',
          'field-wrapper',
          'hidden-field-wrapper',
          'hint-field-wrapper',
          'irembogov-readonly-wrapper',
        ],
      },
    },
    {
      label: 'Generic Data Fetch',
      key: {
        key: 'DATAFETCH',
        type: EIremboFormlyFieldTypes.customgenericdatafetch,
        props: {
          label: 'Generic Data Fetch',
          required: true,
          documentTypeKey: 'DocumentTypeComponentKey',
          placeholder: 'Enter identification number',
          useBaseUrl: true,
          useMultiplePayloadKeys: true,
          endpointCode: 'NIDAGETIDINFO',
          populates: [],
          triggerType: 'TRIGGER_BUTTON',
          url: '/integration/v1/fetch/sync',
          isStatic: false,
        },
        validation: {
          messages: {
            required: 'Error message for `required`',
            invalidInput: 'Error message for invalid input',
            invalidId: 'Error message for failed response',
            pattern: 'Error message for pattern',
          },
        },
      },
    },
    {
      label: 'Location',
      key: LocationWidgetUtils.buildCascadeField(
        `Country${this.randomKey}`,
        <string>Object.keys(LOCATION_WIDGET_LEVELS).shift(),
        <string>Object.keys(LOCATION_WIDGET_LEVELS).pop(),
        'Location Widget',
        false,
        false
      ),
    },
    {
      label: 'NID',
      key: {
        key: 'NID',
        type: EIremboFormlyFieldTypes.customidinput,
        props: {
          useBaseUrl: true,
          label: 'National ID',
          disabled: false,
          readonly: false,
          idLength: CustomIdInputTypesIdLength.NID_ID_LENGTH,
          idType: 'NID',
          required: true,
          placeholder: 'Provide National ID',
          populates: [],
          endpointCode: 'NIDAGETIDINFO',
        },
      },
    },
    {
      label: 'NIN',
      key: {
        key: 'NIN',
        type: EIremboFormlyFieldTypes.customidinput,
        props: {
          label: 'NIN Number',
          placeholder: 'Provide NIN Number',
          required: true,
          readonly: false,
          url: '/identity/v1/nin-info-by-username',
          useBaseUrl: true,
          disabled: false,
          idLength: CustomIdInputTypesIdLength.NIN_ID_LENGTH,
          populateRelativeToRootForm: false,
          populates: [],
        },
        validation: {
          messages: {
            required: 'Error message for `required`',
            invalidInput: 'Error message for invalid input',
            invalidId: 'Error message for invalid NIN',
          },
        },
      },
    },
    {
      label: 'Slot Booking',
      key: buildSlotWidgetField('Slot booking'),
    },
    {
      label: 'TIN',
      key: {
        key: 'TIN',
        type: EIremboFormlyFieldTypes.customtininput,
        defaultValue: null,
        props: {
          endpointCode: 'FTINDETAILS',
          hint: '',
          label: 'TIN',
          hidden: false,
          tooltip: '',
          disabled: false,
          required: false,
          populates: [],
          placeholder: 'Enter TIN',
          defaultRequired: false,
          internalDescription: '',
        },
        validation: {
          messages: {
            required: 'Please fill in this field',
            invalidTIN: 'No details found for supplier',
            invalidInput: 'TIN number must be 9 digits',
          },
        },
      },
    },
    {
      label: 'OTP Data Fetch',
      key: {
        key: 'otpgeneric',
        type: EIremboFormlyFieldTypes.customotpdatafetch,
        props: {
          url: '/integration/v1/fetch/sync',
          label: 'OTP Demo',
          disabled: false,
          required: false,
          otpLength: 5,
          populates: [],
          payloadKey: 'payloadKey',
          useBaseUrl: true,
          placeholder: 'Enter email, telephone(078****) or application number',
          endpointCode: 'TEST_FETCH',
          allowOtpNumbersOnly: false,
        },
        validation: {
          show: true,
          messages: {
            required: 'field is required',
            otpExpired: 'OTP expired.',
            otpInvalid: 'Please enter a valid OTP.',
            otpRequired: 'Please enter the OTP digits sent to the recipient.',
            dataFetchFailed: 'Failed to complete data fetch. Please try again.',
            dataFetchPending: 'Please wait while the information is retrieved.',
            requestOtpFailed: 'Failed sending OTP request. Please try again.',
            pendingRequestOtp:
              'Request for an OTP to be sent to the recipient to proceed.',
            recipientNotFound: 'Recipient not found',
            invalidRecipientEntry: 'Please enter a valid recipient',
            otpVerificationPending:
              'Please submit the OTP for verification to proceed.',
            endpointCodeNotConfigured:
              'Endpoint code is required on this field.',
            otpVerificationRequestFailed: 'OTP verification failed.',
          },
        },
      },
    },
  ];

  subscriptionControl$: Subject<void> = new Subject();

  connectedBlockId: string[] = [];

  formBuilderManipulatorClass!: FormBuilderManipulatorClass;

  EIremboFormlyFieldTypes = EIremboFormlyFieldTypes;

  constructor(
    private formBuilderService: FormBuilderService,
    private changeDetectorRef: ChangeDetectorRef,
    private injector: Injector
  ) {
    this.formBuilderManipulatorClass = new FormBuilderManipulatorClass(
      this.injector
    );
  }

  ngOnInit(): void {
    this.formBuilderService.formFieldBlockIdsSet$
      .pipe(takeUntil(this.subscriptionControl$))
      .subscribe((blockIdSet: Set<string>) => {
        this.connectedBlockId = Array.from(blockIdSet);
        this.changeDetectorRef.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.subscriptionControl$.next();
    this.subscriptionControl$.complete();
  }
}
